import type { Duration } from '@/types';

type Brand<T, Name extends string> = T & { readonly __brand: Name };

type CalendarDateParts = {
  year: number;
  month: number;
  day: number;
};

export type CalendarDate = Brand<string, 'CalendarDate'>;
export type ApiDateTime = Brand<string, 'ApiDateTime'>;

export interface TimeUntilDay {
  months: number;
  days: number;
  calendarDayOffset: number;
}

const CALENDAR_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;
const MS_PER_DAY = 24 * 60 * 60 * 1000;

const padDatePart = (value: number): string => String(value).padStart(2, '0');

const formatDateParts = ({ year, month, day }: CalendarDateParts): CalendarDate =>
  `${year}-${padDatePart(month)}-${padDatePart(day)}` as CalendarDate;

const getDaysInMonth = (year: number, month: number): number =>
  new Date(year, month, 0).getDate();

const parseCalendarDateParts = (value: string): CalendarDateParts | null => {
  const match = CALENDAR_DATE_PATTERN.exec(value);
  if (!match) return null;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);

  if (month < 1 || month > 12) return null;
  if (day < 1 || day > getDaysInMonth(year, month)) return null;

  return { year, month, day };
};

export const calendarDateFromParts = (year: number, month: number, day: number): CalendarDate =>
  formatDateParts({ year, month, day });

const calendarDateToLocalDate = (date: CalendarDate): Date => {
  const parts = parseCalendarDateParts(date);
  if (!parts) return new Date(Number.NaN);

  return new Date(parts.year, parts.month - 1, parts.day);
};

const formatCalendarDate = (
  date: CalendarDate,
  locales?: string | string[],
  options?: Intl.DateTimeFormatOptions
): string => {
  const localDate = calendarDateToLocalDate(date);
  if (Number.isNaN(localDate.getTime())) return '';

  return localDate.toLocaleDateString(locales, options);
};

export const parseCalendarDateInput = (value: string): CalendarDate | null => {
  const datePart = value.split('T')[0];
  const parts = parseCalendarDateParts(datePart);
  return parts ? formatDateParts(parts) : null;
};

export const compareCalendarDates = (left: string, right: string): number => {
  const leftDate = parseCalendarDateInput(left);
  const rightDate = parseCalendarDateInput(right);

  if (!leftDate && !rightDate) return 0;
  if (!leftDate) return -1;
  if (!rightDate) return 1;

  return leftDate.localeCompare(rightDate);
};

export const getTodayForInput = (now: Date = new Date()): CalendarDate =>
  calendarDateFromParts(now.getFullYear(), now.getMonth() + 1, now.getDate());

export const formatDateForDisplay = (
  dateValue: string,
  locales?: string | string[],
  options?: Intl.DateTimeFormatOptions
): string => {
  const calendarDate = parseCalendarDateInput(dateValue);
  return calendarDate ? formatCalendarDate(calendarDate, locales, options) : '';
};

export const apiDateTimeToCalendarDate = (value: string): CalendarDate | null =>
  parseCalendarDateInput(value);

export const calendarDateToApiStart = (date: CalendarDate): ApiDateTime =>
  `${date}T00:00:00.000Z` as ApiDateTime;

export const calendarDateToApiEnd = (date: CalendarDate): ApiDateTime =>
  `${date}T23:59:59.999Z` as ApiDateTime;

export const addCalendarDays = (date: CalendarDate, days: number): CalendarDate => {
  const localDate = calendarDateToLocalDate(date);
  localDate.setDate(localDate.getDate() + days);

  return calendarDateFromParts(
    localDate.getFullYear(),
    localDate.getMonth() + 1,
    localDate.getDate()
  );
};

export const getCalendarWeekday = (date: CalendarDate): number =>
  calendarDateToLocalDate(date).getDay();

const addCalendarMonthsClamped = (date: CalendarDate, monthsToAdd: number): CalendarDate => {
  const parts = parseCalendarDateParts(date);
  if (!parts) return date;

  const targetMonthIndex = parts.month - 1 + monthsToAdd;
  const targetYear = parts.year + Math.floor(targetMonthIndex / 12);
  const targetMonth = ((targetMonthIndex % 12) + 12) % 12 + 1;
  const targetDay = Math.min(parts.day, getDaysInMonth(targetYear, targetMonth));

  return calendarDateFromParts(targetYear, targetMonth, targetDay);
};

const addInclusiveCalendarMonths = (date: CalendarDate, monthsToAdd: number): CalendarDate => {
  const parts = parseCalendarDateParts(date);
  if (!parts) return date;

  const targetMonthIndex = parts.month - 1 + monthsToAdd;
  const targetYear = parts.year + Math.floor(targetMonthIndex / 12);
  const targetMonth = ((targetMonthIndex % 12) + 12) % 12 + 1;
  const targetMonthLastDay = getDaysInMonth(targetYear, targetMonth);

  if (parts.day > targetMonthLastDay) {
    return calendarDateFromParts(targetYear, targetMonth, targetMonthLastDay);
  }

  return addCalendarDays(calendarDateFromParts(targetYear, targetMonth, parts.day), -1);
};

const addInclusiveCalendarYears = (date: CalendarDate, yearsToAdd: number): CalendarDate => {
  const parts = parseCalendarDateParts(date);
  if (!parts) return date;

  const targetYear = parts.year + yearsToAdd;
  const targetMonthLastDay = getDaysInMonth(targetYear, parts.month);

  if (parts.day > targetMonthLastDay) {
    return calendarDateFromParts(targetYear, parts.month, targetMonthLastDay);
  }

  return addCalendarDays(calendarDateFromParts(targetYear, parts.month, parts.day), -1);
};

const getUtcCalendarDayNumber = (date: CalendarDate): number => {
  const parts = parseCalendarDateParts(date);
  if (!parts) return Number.NaN;

  return Date.UTC(parts.year, parts.month - 1, parts.day) / MS_PER_DAY;
};

export const getCalendarDayDifference = (
  startDate: CalendarDate,
  endDate: CalendarDate
): number => {
  return getUtcCalendarDayNumber(endDate) - getUtcCalendarDayNumber(startDate);
};

// Pass durations are inclusive: a one-day pass that starts today also ends today.
// Month/year durations intentionally clamp impossible dates to the last target-month day.
export const calculateEndDate = (
  startDateValue: string,
  duration: Duration
): CalendarDate | null => {
  const startDate = parseCalendarDateInput(startDateValue);
  if (!startDate) return null;

  if (!Number.isInteger(duration.number) || duration.number <= 0) return null;

  switch (duration.measure) {
    case 'Days':
      return addCalendarDays(startDate, duration.number - 1);
    case 'Weeks':
      return addCalendarDays(startDate, duration.number * 7 - 1);
    case 'Months':
      return addInclusiveCalendarMonths(startDate, duration.number);
    case 'Years':
      return addInclusiveCalendarYears(startDate, duration.number);
    default:
      return null;
  }
};

const getCalendarMonthsAndDaysUntilEnd = (
  today: CalendarDate,
  endDate: CalendarDate
): { months: number; days: number } => {
  const exclusiveEndDate = addCalendarDays(endDate, 1);
  let months = 0;

  while (addCalendarMonthsClamped(today, months + 1) <= exclusiveEndDate) {
    months += 1;
  }

  const monthCursor = addCalendarMonthsClamped(today, months);

  return {
    months,
    days: getCalendarDayDifference(monthCursor, exclusiveEndDate),
  };
};

export const getTimeUntilDay = (
  dateValue: string,
  now: Date = new Date()
): TimeUntilDay | null => {
  const endDate = parseCalendarDateInput(dateValue);
  if (!endDate) return null;

  const today = getTodayForInput(now);
  if (today > endDate) return null;

  const endLocalDate = calendarDateToLocalDate(endDate);
  endLocalDate.setHours(23, 59, 59, 999);

  const diffMs = endLocalDate.getTime() - now.getTime();
  if (diffMs < 0) return null;

  const { months, days } = getCalendarMonthsAndDaysUntilEnd(today, endDate);

  return {
    months,
    days,
    calendarDayOffset: getCalendarDayDifference(today, endDate),
  };
};

export interface CalendarDateProgress {
  daysTotal: number;
  daysPassed: number;
  daysLeft: number;
  percentPassed: number;
  percentRemaining: number;
}

const clampNumber = (value: number, min: number, max: number): number =>
  Math.min(Math.max(value, min), max);

export const getCalendarDateProgress = (
  startsAt: string,
  endsAt: string,
  now: Date = new Date()
): CalendarDateProgress | null => {
  const start = parseCalendarDateInput(startsAt);
  const end = parseCalendarDateInput(endsAt);
  if (!start || !end) return null;

  const daysTotal = getCalendarDayDifference(start, end) + 1;
  if (daysTotal <= 0) return null;

  const today = getTodayForInput(now);
  const elapsed = getCalendarDayDifference(start, today);
  const daysPassed = clampNumber(elapsed, 0, daysTotal);
  const daysLeft = daysTotal - daysPassed;
  const percentPassed = clampNumber((daysPassed / daysTotal) * 100, 0, 100);
  const percentRemaining = 100 - percentPassed;

  return { daysTotal, daysPassed, daysLeft, percentPassed, percentRemaining };
};

export const formatDateForInput = (dateValue: string): string =>
  parseCalendarDateInput(dateValue) ?? '';

export const getCalendarDateKey = formatDateForInput;

export const parseCalendarDate = (dateValue: string): Date => {
  const calendarDate = parseCalendarDateInput(dateValue);
  return calendarDate ? calendarDateToLocalDate(calendarDate) : new Date(Number.NaN);
};

export const normalizeDateToStart = (dateValue: string): string => {
  const calendarDate = parseCalendarDateInput(dateValue);
  return calendarDate ? calendarDateToApiStart(calendarDate) : '';
};

export const normalizeDateToEnd = (dateValue: string): string => {
  const calendarDate = parseCalendarDateInput(dateValue);
  return calendarDate ? calendarDateToApiEnd(calendarDate) : '';
};
